import { useState } from 'react';
import moment from 'moment';
import { Match } from '@views/WarehouseDetails/WarehouseDetails';
import { HiDownload } from 'react-icons/hi';
import { BillingCycle, Invoice } from './invoiceTypes';
import { useAuthHttp } from '../../hooks';
import { INVOICE_PDF } from '../../constants';

export function InvoiceSummary({
  isLsp,
  hasConfirmed,
  hasPaid,
  match,
  billingCycle,
  invoice,
}: {
  isLsp?: boolean;
  hasConfirmed?: boolean;
  hasPaid?: boolean;
  match: Match | undefined;
  billingCycle: BillingCycle;
  invoice: Invoice;
}) {
  const authHttp = useAuthHttp();
  const [isDownloading, setIsDownloading] = useState(false);

  const startDate = moment(billingCycle.startDate);
  const endDate = moment(billingCycle.endDate);
  const billingCycleTerms = endDate.diff(startDate, 'days');

  const onDownloadInvoice = async () => {
    setIsDownloading(true);
    try {
      const response = await authHttp.post(
        INVOICE_PDF,
        { billingCycleId: billingCycle.id },
        {
          headers: { 'Content-Type': 'application/json' },
          responseType: 'blob',
        }
      );

      let filename = `invoice-${invoice.invoiceNumber}-${moment().format('DD-MM-YYYY')}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download invoice PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusBadge = () => {
    if (!hasConfirmed && isLsp) {
      return (
        <span className='inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20'>
          Proforma
        </span>
      );
    }
    if (hasConfirmed && !hasPaid) {
      return (
        <span className='inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20'>
          {isLsp ? 'Pending Payment' : 'Awaiting Payment'}
        </span>
      );
    }
    if (hasPaid) {
      return (
        <span className='inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'>
          Paid
        </span>
      );
    }
    return null;
  };

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative'>
      {/* Header with status and download */}
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center gap-4'>
          <h2 className='text-xl font-semibold text-gray-900'>
            {!hasConfirmed && isLsp && 'PROFORMA INVOICE'}
            {hasConfirmed && !hasPaid && 'INVOICE'}
            {hasPaid && 'INVOICE'}
          </h2>
          {getStatusBadge()}
        </div>
        <button
          type='button'
          onClick={() => onDownloadInvoice()}
          disabled={isDownloading}
          className='inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <HiDownload className='w-4 h-4' />
          {isDownloading ? 'Downloading...' : 'Download'}
        </button>
      </div>

      {/* Invoice Number */}
      <div className='mb-6'>
        <span className='text-sm text-gray-500'>Invoice Number</span>
        <p className='text-lg font-medium text-gray-900'>
          {invoice.invoiceNumber}
        </p>
      </div>

      {/* Two column layout */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
        {/* Bill To */}
        <div className='space-y-4'>
          <div className='border-b border-gray-200 pb-2'>
            <span className='text-sm font-medium text-gray-500 uppercase tracking-wider'>
              {isLsp ? 'Bill To (via FLOX)' : 'Bill To'}
            </span>
          </div>
          <div className='space-y-2'>
            <p className='text-base font-semibold text-gray-900'>
              {billingCycle.shipperBusinessName}
            </p>
            <p className='text-sm text-gray-600'>
              {billingCycle.shipperBusinessAddress}
            </p>
          </div>
          <div className='pt-2'>
            <span className='text-sm text-gray-500'>Invoice Date</span>
            <p className='text-sm font-medium text-gray-900'>
              {!hasConfirmed && moment(invoice.createdAt).format('DD/MM/YYYY')}
              {hasConfirmed &&
                !hasPaid &&
                moment(invoice.approvedAt).format('DD/MM/YYYY')}
              {hasPaid && moment(invoice.createdAt).format('DD/MM/YYYY')}
            </p>
          </div>
        </div>

        {/* From */}
        <div className='space-y-4'>
          <div className='border-b border-gray-200 pb-2'>
            <span className='text-sm font-medium text-gray-500 uppercase tracking-wider'>
              {isLsp ? 'From' : 'Service Provider'}
            </span>
          </div>
          <div className='space-y-2'>
            <p className='text-base font-semibold text-gray-900'>
              {billingCycle.lspBusinessName}
            </p>
            <p className='text-sm text-gray-600'>
              {billingCycle.lspBusinessAddress}
            </p>
            <p className='text-sm text-gray-600'>
              Company No: {billingCycle.lspCompanyNumber}
            </p>
            {isLsp && (
              <p className='text-sm text-gray-600'>
                VAT No: {billingCycle.lspVatNumber}
              </p>
            )}
          </div>
          <div className='pt-2 grid grid-cols-2 gap-4'>
            <div>
              <span className='text-sm text-gray-500'>Terms</span>
              <p className='text-sm font-medium text-gray-900'>
                {hasConfirmed
                  ? `${match?.paymentTermsInDays} days`
                  : `${billingCycleTerms} days`}
              </p>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Currency</span>
              <p className='text-sm font-medium text-gray-900'>
                {invoice.lines[0].currencyCode} (
                {invoice.lines[0].currencySymbol})
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
